// Evaluator branch for `epx:MachineLearningDecision`. Same shape as the FEEL
// evaluator's other branches (returns the value the decision resolves to)
// but interposes an external invocation: each inputBinding.source is FEEL,
// the resolved features are sent to ctx.mlInvoker, and the result is mapped
// back through outputBindings.
//
// The host wires `ctx.mlInvoker` (DmnSimulator does this from the simulator
// side) and `ctx.evalFeel` so this module stays free of direct simulator /
// FEEL evaluator imports — keeps the dependency graph one-way.
//
// Errors and below-threshold results take the configured fallback. A failing
// fallback re-throws — this is intentional: a misconfigured ML decision must
// surface clearly in the trace rather than silently produce null.

export class MlEvalError extends Error {
  constructor(msg) { super(`[feel/ml] ${msg}`); }
}

function applyFallback(fallback, err, scope, ctx) {
  if (!fallback || !fallback.strategy) {
    // No fallback → propagate the original error so the simulator records
    // it in trace + warnings.
    throw err;
  }
  const strat = fallback.strategy;
  if (strat === 'error') {
    throw err;
  }
  if (strat === 'value') {
    const text = fallback.value;
    if (!text || !String(text).trim()) return null;
    if (typeof ctx.evalFeel !== 'function') {
      throw new MlEvalError('fallback strategy "value" requires ctx.evalFeel');
    }
    return ctx.evalFeel(text, scope);
  }
  if (strat === 'rule') {
    // Phase 1: resolve to a sibling Decision/BKM by name through the scope.
    // The variable.name is exposed in `scope` (FEEL env), so look it up
    // directly. If absent, throw — we don't silently swallow.
    const name = String(fallback.value || '').trim();
    if (!name) throw new MlEvalError('fallback strategy "rule" requires a target name');
    const v = scope && Object.prototype.hasOwnProperty.call(scope, name) ? scope[name] : undefined;
    if (typeof v === 'function') return v(); // BKM closure
    if (v === undefined) throw new MlEvalError(`fallback rule '${name}' not found in scope`);
    return v;
  }
  throw new MlEvalError(`unknown fallback strategy '${strat}'`);
}

/**
 * @param {object} logic   moddle epx:MachineLearningDecision element
 * @param {object} scope   FEEL env (the same `context` the simulator passes)
 * @param {object} ctx     { evalFeel(text, scope), mlInvoker }
 * @returns {Promise<any>} scalar (single outputBinding) or context (>1)
 */
export async function evaluateMlDecision(logic, scope, ctx) {
  if (!logic) throw new MlEvalError('logic is required');
  if (!ctx || typeof ctx.evalFeel !== 'function') {
    throw new MlEvalError('ctx.evalFeel is required');
  }
  if (!ctx.mlInvoker || typeof ctx.mlInvoker.invoke !== 'function') {
    throw new MlEvalError('ctx.mlInvoker.invoke is required');
  }

  // Resolve features. Any FEEL parse / lookup error propagates immediately —
  // misconfigured bindings are caller errors, not service errors, so they
  // shouldn't trigger the fallback path.
  const features = {};
  for (const b of logic.inputBindings || []) {
    if (!b || !b.feature) continue;
    const src = b.source;
    features[b.feature] = (src && String(src).trim())
      ? ctx.evalFeel(src, scope)
      : null;
  }

  let result;
  try {
    result = await ctx.mlInvoker.invoke({
      runtime: logic.runtime,
      endpoint: logic.endpoint,
      modelRef: logic.modelRef,
      modelVersion: logic.modelVersion,
      features,
    });
  } catch (err) {
    return applyFallback(logic.fallback, err, scope, ctx);
  }

  if (!result || typeof result !== 'object') {
    return applyFallback(logic.fallback, new MlEvalError('invoker returned no result'), scope, ctx);
  }

  if (logic.confidenceThreshold != null
      && typeof result.confidence === 'number'
      && result.confidence < logic.confidenceThreshold) {
    return applyFallback(logic.fallback, new MlEvalError('below_threshold'), scope, ctx);
  }

  const outs = logic.outputBindings || [];
  const outputs = result.outputs || {};
  if (outs.length === 1) return outputs[outs[0].field];
  if (outs.length === 0) return outputs;
  return Object.fromEntries(outs.map((o) => [o.field, outputs[o.field]]));
}
